import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Stack,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import { useApiClient } from "../useApiClient";
import useAuthToken from '../../functions/useAuthToken';
import { useUser } from "../../components/UserContext";
import type {
    BillingInterval,
    BillingStatusDto,
    ChangePlanRequest,
    LicensingPlanDto,
    SubscriptionApplyResult,
    SubscriptionHistoryItemDto,
    SubscriptionQuoteDto,
    SubscriptionQuoteRequestDto,
    TenantHealth,
} from '../../models/License';
import type { TenantDto, TenantLicenseDto, TenantUsageDto } from '../../models/Tenant';

import SubscriptionPlans from "./SubscriptionPlans";
import TenantUsageWidget from "./TenantUsageWidget";
import ChangePlanDialog from "./ChangePlanDialog";
import SubscriptionQuoteDialog from "./SubscriptionQuoteDialog";
import SubscriptionHistoryTable from "./SubscriptionHistoryTable";

type Notice = { severity: "success" | "info" | "warning" | "error"; text: string };

function formatDate(value?: string | null) {
    if (!value) return "";
    return new Date(value).toLocaleDateString();
}

function getLicenseSeverity(license: TenantLicenseDto): "success" | "warning" | "error" {
    if (license.isExpired) return "error";
    if (license.isExpiringSoon) return "warning";
    return "success";
}

/**
 * Pulls the API's message out of an error. 409/400 responses carry { code, message } or a plain string.
 * Adjust to match how useApiClient exposes response bodies if it differs.
 */
function getErrorMessage(err: any, fallback: string): string {
    const body = err?.response?.data ?? err?.body ?? err?.data;
    if (typeof body === "string" && body.trim()) return body;
    if (body?.message) return body.message;
    if (body?.Message) return body.Message;
    return err?.message ?? fallback;
}

const describeInterval = (interval?: BillingInterval | null) =>
    interval === "month" ? "Monthly" : interval === "year" ? "Annual" : "—";

type InfoCardProps = {
    title: string;
    children: React.ReactNode;
};

const InfoCard: React.FC<InfoCardProps> = ({ title, children }) => (
    <Card sx={{
        height: "100%", width: "100%",
    }}>
        <CardContent>
            <Typography variant="h6">{title}</Typography>
            <Divider sx={{ my: 1.5 }} />
            {children}
        </CardContent>
    </Card>
);

const LicenseManagementPage: React.FC = () => {
    const { get, postJson } = useApiClient();
    const getAccessToken = useAuthToken();
    const { user, refreshUser } = useUser();

    const [tenant, setTenant] = useState<TenantDto | null>(null);
    const [tenantHealth, setTenantHealth] = useState<TenantHealth | undefined>();
    const [license, setLicense] = useState<TenantLicenseDto | null>(null);
    const [usage, setUsage] = useState<TenantUsageDto | null>(null);
    const [plans, setPlans] = useState<LicensingPlanDto[]>([]);
    const [billing, setBilling] = useState<BillingStatusDto | null>(null);
    const [quote, setQuote] = useState<SubscriptionQuoteDto | null>(null);
    const [history, setHistory] = useState<SubscriptionHistoryItemDto[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<LicensingPlanDto | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Monthly / Annual toggle. Starts on the subscriber's current period, or monthly for new customers.
    const [interval, setInterval] = useState<BillingInterval>("month");
    const intervalInitialised = useRef(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);

    const [loadingQuote, setLoadingQuote] = useState(false);
    const [applying, setApplying] = useState(false);
    const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const fetchBillingStatus = useCallback(async (accessToken: string) => {
        try {
            return await get<BillingStatusDto>("billing/status", accessToken);
        } catch {
            return null; // billing info is optional; the page still works without it
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const [tenantRes, licenseRes, usageRes, plansRes, subsHistory, billingRes] = await Promise.all([
                get<TenantDto>(`tenant/details`, accessToken),
                get<TenantLicenseDto>(`tenant/license`, accessToken),
                get<TenantUsageDto>(`tenant/usage`, accessToken),
                get<LicensingPlanDto[]>(`licensing/plans`, accessToken),
                get<SubscriptionHistoryItemDto[]>("tenant/history", accessToken),
                fetchBillingStatus(accessToken),
            ]);

            setTenant(tenantRes);
            setLicense(licenseRes);
            setUsage(usageRes);
            setPlans(plansRes);
            setHistory(subsHistory);
            setBilling(billingRes);

            if (!intervalInitialised.current && billingRes?.billingInterval) {
                setInterval(billingRes.billingInterval);
            }
            intervalInitialised.current = true;

        } catch (err: any) {
            setError(err?.message ?? "Failed to load tenant console.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (!user) return;
        if (user?.tenantHealth) {
            setTenantHealth(user.tenantHealth);
        }
    }, [user]);

    // Returning from Stripe Checkout: ?checkout=success | cancelled
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const checkout = params.get("checkout");
        if (!checkout) return;

        params.delete("checkout");
        const query = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash);

        if (checkout === "cancelled") {
            setNotice({ severity: "info", text: "Checkout was cancelled. Your plan hasn't changed." });
            return;
        }
        if (checkout !== "success") return;

        // The licence is created by Stripe's webhook, usually within a few seconds. Poll until it shows.
        setNotice({ severity: "info", text: "Payment received. Activating your plan..." });
        let cancelled = false;

        (async () => {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            for (let attempt = 0; attempt < 15 && !cancelled; attempt++) {
                const status = await fetchBillingStatus(accessToken);
                if (status?.hasSubscription) {
                    if (cancelled) return;
                    setNotice({ severity: "success", text: "Your subscription is active. Thank you!" });
                    refreshUser();
                    await loadData();
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
            if (!cancelled) {
                setNotice({
                    severity: "warning",
                    text: "Your payment went through, but activation is taking longer than usual. Refresh in a minute; if your plan still hasn't changed, please contact support.",
                });
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const handleSelectPlan = async (plan: typeof plans[number]) => {
        setSelectedPlan(plan);

        // The trial isn't sold through Stripe: keep the existing licensing/change path for it.
        if (plan.tierCode.toUpperCase() === "TRIAL") {
            setDialogOpen(true);
            return;
        }

        requestQuote("UPGRADE", plan.licensingPlanId);
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    // Starts the free trial (server decides the length and allows it once per tenant).
    const handleApplyPlan = useCallback(async (newLicenseTier: string) => {

        setSaving(true);
        setNotice(null);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const newLicense: ChangePlanRequest = {
                tierCode: newLicenseTier,
            }

            await postJson("licensing/change", accessToken, newLicense);
            refreshUser();

            await loadData();

        } catch (err: any) {
            setNotice({ severity: "error", text: getErrorMessage(err, "Failed to start the trial.") });
        } finally {
            setSaving(false);
        }
    }, [loadData]);

    const handleConfirmChange = async () => {
        if (!selectedPlan) return;

        try {
            await handleApplyPlan(selectedPlan.tierCode);

        } finally {
            setDialogOpen(false);
            setSelectedPlan(null);
            setSaving(false);
        }
    };

    const requestQuote = async (actionType: "RENEW" | "UPGRADE", targetLicensingPlanId?: number) => {
        setLoadingQuote(true);
        setSaving(true);
        setNotice(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const quoteRequest: SubscriptionQuoteRequestDto = {
                actionType: actionType,
                targetLicensingPlanId: targetLicensingPlanId ?? null,
                billingInterval: actionType === "UPGRADE" ? interval : undefined,
            }

            const result = await postJson<SubscriptionQuoteDto>("licensing/quote", accessToken, quoteRequest);

            setQuote(result);
            setQuoteDialogOpen(true);
        } catch (err: any) {
            setNotice({ severity: "error", text: getErrorMessage(err, "Couldn't get a price for that change.") });
        } finally {
            setLoadingQuote(false);
            setSaving(false);
        }
    };

    const applyQuote = async () => {
        if (!quote) return;

        setApplying(true);
        setSaving(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const quoteRequest: SubscriptionQuoteRequestDto = {
                actionType: quote.actionType,
                targetLicensingPlanId: quote.targetLicensingPlanId,
                billingInterval: quote.targetBillingInterval ?? undefined,
            }

            const result = await postJson<SubscriptionApplyResult>("licensing/apply", accessToken, quoteRequest);

            // First purchase or re-subscribe: card details are taken on Stripe Checkout.
            if (result?.checkoutUrl) {
                window.location.assign(result.checkoutUrl);
                return;
            }

            setQuoteDialogOpen(false);
            setQuote(null);

            const severity: Notice["severity"] =
                result?.outcome === "pending_payment" ? "warning"
                    : result?.outcome === "scheduled" || result?.outcome === "auto_renews" ? "info"
                        : "success";
            if (result?.message) setNotice({ severity, text: result.message });

            // reload tenant/license/health state
            refreshUser();
            await loadData();
        } catch (err: any) {
            setQuoteDialogOpen(false);
            setNotice({ severity: "error", text: getErrorMessage(err, "The change couldn't be applied.") });
        } finally {
            setApplying(false);
            setSaving(false);
        }
    };

    // Stripe Customer Portal: card, billing details / VAT number, invoices, cancel.
    const openBillingPortal = async () => {
        setSaving(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await postJson<{ url: string }>("billing/portal", accessToken, {});
            window.location.assign(result.url);
        } catch (err: any) {
            setNotice({ severity: "error", text: getErrorMessage(err, "Couldn't open billing.") });
            setSaving(false);
        }
    };

    const cancelPendingChange = async () => {
        setSaving(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await postJson<{ message: string }>("billing/pending/cancel", accessToken, {});
            setNotice({ severity: "success", text: result?.message ?? "Scheduled change cancelled." });
            refreshUser();
            await loadData();
        } catch (err: any) {
            setNotice({ severity: "error", text: getErrorMessage(err, "Couldn't cancel the scheduled change.") });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box p={3} display="flex" alignItems="center" gap={2}>
                <CircularProgress size={22} />
                <Typography>Loading license management console...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={loadData}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (!tenant || !license || !usage) {
        return (
            <Box p={3}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Tenant information is incomplete.
                </Alert>
                <Button variant="outlined" onClick={loadData}>
                    Reload
                </Button>
            </Box>
        );
    }

    const licenseSeverity = getLicenseSeverity(license);
    const isTrial = (license.tier ?? "").toUpperCase() === "TRIAL";
    const autoRenews = !!billing?.hasSubscription && !billing.cancelAtPeriodEnd && !billing.paymentIssue;

    // Renew is only needed when nothing renews by itself: a cancelled subscription, or a lapsed / manual paid plan.
    const showRenew = !!billing?.cancelAtPeriodEnd || (!billing?.hasSubscription && !isTrial);
    const renewLabel = billing?.cancelAtPeriodEnd ? "Resume Subscription" : "Renew Subscription";

    return (
        <>
            <Box p={3}>
                <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                        <Box>
                            <Typography variant="h5">License Management Console</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your subscription, plan and current usage.
                            </Typography>
                        </Box>

                        {billing?.canManageBilling && (
                            <Button variant="outlined" onClick={openBillingPortal} disabled={saving}>
                                Manage billing
                            </Button>
                        )}
                    </Box>

                    {notice && (
                        <Alert severity={notice.severity} onClose={() => setNotice(null)}>
                            {notice.text}
                        </Alert>
                    )}

                    {billing?.paymentIssue && (
                        <Alert
                            severity="error"
                            action={<Button color="inherit" size="small" onClick={openBillingPortal} disabled={saving}>Update card</Button>}
                        >
                            Your last payment didn't go through. Update your card to keep your subscription active.
                        </Alert>
                    )}

                    {billing?.cancelAtPeriodEnd && !billing.paymentIssue && (
                        <Alert
                            severity="warning"
                            action={
                                <Button color="inherit" size="small" onClick={() => requestQuote("RENEW")} disabled={saving || loadingQuote}>
                                    Resume subscription
                                </Button>
                            }
                        >
                            Your subscription is cancelled and ends on{" "}
                            <strong>{formatDate(billing.renewsOrEndsOnUtc ?? license.effectiveToUtc)}</strong>.
                            You keep full access until then. Resume to keep it renewing.
                        </Alert>
                    )}

                    {billing?.pendingChange && (
                        <Alert
                            severity="info"
                            action={<Button color="inherit" size="small" onClick={cancelPendingChange} disabled={saving}>Keep current plan</Button>}
                        >
                            On {formatDate(billing.pendingChange.effectiveAtUtc)} your plan changes to{" "}
                            <strong>{billing.pendingChange.planName}</strong> ({describeInterval(billing.pendingChange.billingInterval).toLowerCase()}).
                        </Alert>
                    )}

                    <Alert severity={licenseSeverity}>
                        {license.statusMessage}
                    </Alert>

                    <Grid container spacing={3}>
                        <Box
                            sx={{
                                width: { xs: '100%', md: 'calc(33.333% - 16px)' },
                                flexGrow: 1
                            }}
                        >
                            <InfoCard title="Subscription">
                                <Stack spacing={1.25}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Name</Typography>
                                        <Typography variant="body1">{tenant.name}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Created</Typography>
                                        <Typography variant="body1">{formatDate(tenant.createdAtUtc)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Status</Typography>
                                        <Typography variant="body1">{tenant.isActive ? "Active" : "Inactive"}</Typography>
                                    </Box>
                                </Stack>
                            </InfoCard>
                        </Box>

                        <Box sx={{
                            width: { xs: '100%', md: 'calc(33.333% - 16px)' },
                            flexGrow: 1
                        }}>
                            <InfoCard title="License">
                                <Stack spacing={1.25}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Tier</Typography>
                                        <Typography variant="body1">{license.tier}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Plan</Typography>
                                        <Typography variant="body1">{license.planName}</Typography>
                                    </Box>
                                    {billing?.hasSubscription && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Billing</Typography>
                                            <Typography variant="body1">{describeInterval(billing.billingInterval)}</Typography>
                                        </Box>
                                    )}
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Effective From</Typography>
                                        <Typography variant="body1">{formatDate(license.effectiveFromUtc)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">{autoRenews ? "Renews" : "Expires"}</Typography>
                                        <Typography variant="body1">{formatDate(license.effectiveToUtc)}</Typography>
                                    </Box>
                                    {!autoRenews && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Days Remaining</Typography>
                                            <Typography variant="body1">{license.daysRemaining}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </InfoCard>
                        </Box>

                        {tenantHealth &&
                            <Box sx={{
                                width: { xs: '100%', md: 'calc(33.333% - 16px)' },
                                flexGrow: 1
                            }}>
                                <TenantUsageWidget health={tenantHealth} />
                            </Box>
                        }
                    </Grid>


                    <Box sx={{ width: { xs: '100%', md: '100%' }, flexGrow: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <SubscriptionPlans
                            plans={plans}
                            currentTierCode={license.tier}
                            currentInterval={billing?.hasSubscription ? billing.billingInterval : null}
                            pendingChange={billing?.pendingChange}
                            interval={interval}
                            onIntervalChange={setInterval}
                            alreadyUsedTrail={license.alreadyHadTrail}
                            loading={saving}
                            onSelectPlan={handleSelectPlan}
                        />
                    </Box>

                    {!isMobile &&
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <SubscriptionHistoryTable items={history} />
                        </Box>
                    }

                    {showRenew &&
                        <Box>
                            <Button
                                variant="outlined"
                                onClick={() => requestQuote("RENEW")}
                                disabled={loadingQuote || saving}
                            >
                                {renewLabel}
                            </Button>
                        </Box>
                    }
                </Stack>
            </Box>

            <SubscriptionQuoteDialog
                open={quoteDialogOpen}
                quote={quote}
                loading={applying}
                onClose={() => {
                    if (applying) return;
                    setQuoteDialogOpen(false);
                }}
                onConfirm={applyQuote}
            />

            <ChangePlanDialog
                open={dialogOpen}
                currentPlan={{
                    tierName: license.tier,
                    planName: license.planName,
                    effectiveToUtc: license.effectiveToUtc,
                }}
                selectedPlan={selectedPlan}
                loading={saving}
                onClose={handleCloseDialog}
                onConfirm={handleConfirmChange}
            />

        </>
    );
};

export default LicenseManagementPage;
